import type { CannedResponse } from "../api";

/**
 * Which saved replies fit what the customer just wrote.
 *
 * Plain word overlap, done in the browser: the operator's saved replies are
 * already loaded, and nothing about a customer's message needs to leave the
 * console to be matched against them. Words match when one begins with the
 * other (at least four letters in common), so Turkish endings do not hide a
 * match — "lisansımı" meets "lisans", "iadem" meets "iade".
 */

const STOP = new Set([
  // Turkish
  "bir", "bu", "şu", "ve", "ile", "için", "ama", "çok", "daha", "gibi", "var", "yok", "ben", "sen", "biz",
  "siz", "mı", "mi", "mu", "mü", "ne", "nasıl", "neden", "merhaba", "selam", "lütfen", "teşekkür", "teşekkürler",
  "tamam", "evet", "hayır", "olarak", "olan", "oldu", "olur", "yardım", "istiyorum", "rica",
  // English
  "the", "and", "for", "you", "your", "with", "this", "that", "have", "has", "not", "can", "how", "what",
  "why", "hello", "hi", "please", "thanks", "thank", "help", "want", "need", "are", "was", "but",
]);

function words(text: string): string[] {
  const all = text.toLocaleLowerCase("tr").match(/[\p{L}\p{N}]{3,}/gu) ?? [];
  return [...new Set(all.filter((w) => !STOP.has(w)))];
}

function related(a: string, b: string): boolean {
  if (a === b) return true;
  const [short, long] = a.length <= b.length ? [a, b] : [b, a];
  return short.length >= 4 && long.startsWith(short);
}

export function suggestCanned(all: CannedResponse[], customerText: string | null | undefined, limit = 3): CannedResponse[] {
  if (!customerText) return [];
  const wanted = words(customerText);
  if (wanted.length === 0) return [];

  const scored = all.map((item) => {
    const head = words(`${item.shortcut} ${item.title}`);
    const body = words(item.body);
    let score = 0;
    for (const word of wanted) {
      // A hit in the title or shortcut says what the reply is *for*; the body only mentions it.
      if (head.some((w) => related(w, word))) score += 3;
      else if (body.some((w) => related(w, word))) score += 1;
    }
    return { item, score };
  });

  return scored
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score || b.item.usageCount - a.item.usageCount)
    .slice(0, limit)
    .map((s) => s.item);
}
